import { getErrorCode, getErrorMessageOrDefault, unknownToError } from "@vortex/shared";
import PromiseBB from "bluebird";
import * as path from "path";
import turbowalk from "turbowalk";
import * as util from "util";
import * as winapi from "winapi-bindings";
import { setSettingsPage } from "../../actions/session";
import { log } from "../../logging";
import * as fs from "../../util/fs";
import { installPathForGame } from "../../util/selectors";
import { getGame } from "../gamemode_management/util/getGame";
import LinkingDeployment from "../mod_management/LinkingDeployment";
export class FileFound extends Error {
    constructor(name) {
        super(name);
        this.name = this.constructor.name;
    }
}
class DeploymentMethod extends LinkingDeployment {
    priority = 5;
    mInstallationFiles;
    constructor(api) {
        super("hardlink_activator", "Hardlink Deployment", "Deploys mods by setting hard links in the destination directory.", true, api);
    }
    detailedDescription(t) {
        return t("File Systems store files in two parts: \n" +
            " - an index entry that contains the file name, " +
            "access rights, change and creating times and so on\n" +
            " - the actual file data\n" +
            "Hard Links work by creating a second index entry referencing " +
            "the same data as the original. The second index is " +
            'a full-fledged index, so there is no differentiation between "original" and "link" ' +
            "after the link was created.\n" +
            "Advantages:\n" +
            " - perfect compatibility with all applications\n" +
            " - no performance penalty\n" +
            " - Wide OS and FS support\n" +
            "Disadvantages:\n" +
            " - mods have to be on the same partition as the game\n" +
            ' - Due to fact hard links are so "compatible", a lot of applications will act ' +
            "as if original and link were separate files. This includes some backup solutions, tools " +
            "that measure used disk space and so on, so it will often look like the link was actually " +
            "a copy.");
    }
    isSupported(state, gameId, typeId) {
        const discovery = state.settings.gameMode.discovered[gameId];
        if (discovery?.path == null) {
            return {
                description: (t) => t("Game not discovered."),
            };
        }
        const game = getGame(gameId);
        const modPaths = game.getModPaths(discovery.path);
        if (modPaths[typeId] === undefined) {
            return undefined;
        }
        try {
            fs.accessSync(modPaths[typeId], fs.constants.W_OK);
        }
        catch {
            log("info", "hardlink deployment not supported due to lack of write access", { typeId, path: modPaths[typeId] });
            return {
                description: (t) => t("Can't write to output directory."),
                order: 3,
                solution: (t) => t("To resolve this problem, the current user account needs to be given " +
                    'write permission to "{{modPath}}".', {
                    replace: {
                        modPath: modPaths[typeId],
                    },
                }),
            };
        }
        const installationPath = installPathForGame(state, gameId);
        try {
            if (fs.statSync(installationPath).dev !== fs.statSync(modPaths[typeId]).dev) {
                // hard links work only on the same drive
                return {
                    description: (t) => t("Works only if mods are installed on the same drive as the game."),
                    order: 5,
                    solution: (t) => {
                        let displayPath = modPaths[typeId];
                        try {
                            displayPath = winapi.GetVolumePathName(modPaths[typeId]);
                        }
                        catch {
                            log("warn", "Failed to resolve volume path", {
                                path: modPaths[typeId],
                            });
                        }
                        return t("Please go to Settings->Mods and set the mod staging folder to be on " +
                            "the same drive as the game ({{gameVolume}}).", {
                            replace: {
                                gameVolume: displayPath,
                            },
                        });
                    },
                    fixCallback: (api) => new PromiseBB((_resolve, _reject) => {
                        api.events.emit("show-main-page", "application_settings");
                        api.store.dispatch(setSettingsPage("Mods"));
                        api.highlightControl("#install-path-form", 5000, "Change this to be on the same drive as the game.");
                    }),
                };
            }
        }
        catch (err) {
            // this can happen when managing the the game for the first time
            log("info", "failed to stat. directory missing?", {
                dir1: installationPath || "undefined",
                dir2: modPaths[typeId],
                err: util.inspect(err),
            });
            return {
                description: (t) => t("Game not fully initialized yet, this should disappear soon."),
            };
        }
        const canary = path.join(installationPath, "__vortex_canary.tmp");
        let res;
        try {
            try {
                fs.removeSync(canary + ".link");
            }
            catch {
                // nop
            }
            fs.writeFileSync(canary, "Should only exist temporarily, feel free to delete");
            fs.linkSync(canary, canary + ".link");
        }
        catch (err) {
            // EMFILE shouldn't keep us from using hard linking
            const code = getErrorCode(err);
            if (code !== "EMFILE") {
                // the error code we're actually getting is EISDIR, which makes no sense at all
                res = {
                    description: (t) => t("Filesystem doesn't support hard links."),
                };
            }
        }
        try {
            fs.removeSync(canary + ".link");
            fs.removeSync(canary);
        }
        catch {
            // cleanup failed, this is almost certainly due to an AV jumping in to check these new files,
            // I mean, why would I be able to create the files but not delete them?
            // just try again later - can't do that synchronously though
            PromiseBB.delay(100)
                .then(() => fs.removeAsync(canary + ".link"))
                .then(() => fs.removeAsync(canary))
                .catch((err) => {
                log("error", "failed to clean up canary file. This indicates we were able to create " +
                    "a file in the target directory but not delete it", { installationPath, message: getErrorMessageOrDefault(err) });
            });
        }
        return res;
    }
    finalize(gameId, dataPath, installationPath, progressCB) {
        return super.finalize(gameId, dataPath, installationPath, progressCB);
    }
    postPurge() {
        delete this.mInstallationFiles;
        this.mInstallationFiles = undefined;
        return PromiseBB.resolve();
    }
    purgeLinks(installationPath, dataPath, onProgress) {
        let installEntryProm;
        // find ids of all files in our mods directory
        // using idStr (string) instead of id (number) to avoid precision loss
        // for NTFS file IDs exceeding Number.MAX_SAFE_INTEGER
        if (this.mInstallationFiles !== undefined) {
            installEntryProm = PromiseBB.resolve(this.mInstallationFiles);
        }
        else {
            this.mInstallationFiles = new Set();
            installEntryProm = turbowalk(installationPath, (entries) => {
                if (this.mInstallationFiles === undefined) {
                    // don't know when this would be necessary but apparently
                    // it is, see https://github.com/Nexus-Mods/Vortex/issues/3684
                    return;
                }
                entries.forEach((entry) => {
                    if (entry.linkCount > 1 && entry.idStr !== undefined) {
                        this.mInstallationFiles.add(entry.idStr);
                    }
                });
            }, {
                details: true,
                skipHidden: false,
            })
                .catch((err) => ["ENOENT", "ENOTFOUND"].includes(err.code)
                ? PromiseBB.resolve()
                : PromiseBB.reject(err))
                .then(() => PromiseBB.resolve(this.mInstallationFiles));
        }
        // now remove all files in the game directory that have the same id
        // as a file in the mods directory
        return installEntryProm.then((inos) => {
            const total = inos.size;
            let purged = 0;
            let queue = PromiseBB.resolve();
            if (inos.size === 0) {
                return PromiseBB.resolve();
            }
            return turbowalk(dataPath, (entries) => {
                queue = queue.then(() => PromiseBB.map(entries, (entry) => {
                    if (entry.linkCount > 1 &&
                        entry.idStr !== undefined &&
                        inos.has(entry.idStr)) {
                        ++purged;
                        if (purged % 1000 === 0) {
                            onProgress?.(purged, total);
                        }
                        return fs
                            .unlinkAsync(entry.filePath)
                            .catch((err) => log("warn", "failed to remove", entry.filePath));
                    }
                    else {
                        return PromiseBB.resolve();
                    }
                }).then(() => undefined));
            }, { details: true, skipHidden: false }).then(() => queue);
        });
    }
    linkFile(linkPath, sourcePath, dirTags) {
        return this.ensureDir(path.dirname(linkPath), dirTags)
            .then(() => fs.linkAsync(sourcePath, linkPath))
            .catch((err) => getErrorCode(err) !== "EEXIST"
            ? Promise.reject(unknownToError(err))
            : fs
                .removeAsync(linkPath)
                .then(() => fs.linkAsync(sourcePath, linkPath)));
    }
    unlinkFile(linkPath) {
        return fs.unlinkAsync(linkPath);
    }
    isLink(linkPath, sourcePath, linkStatsIn, sourceStatsIn) {
        if (linkStatsIn !== undefined && sourceStatsIn !== undefined) {
            return PromiseBB.resolve(linkStatsIn.nlink > 1 && linkStatsIn.ino === sourceStatsIn.ino);
        }
        return fs
            .lstatAsync(linkPath)
            .then((linkStats) => linkStats.nlink === 1
            ? PromiseBB.resolve(false)
            : fs
                .lstatAsync(sourcePath)
                .then((sourceStats) => linkStats.ino === sourceStats.ino))
            .catch((err) => getErrorCode(err) === "ENOENT"
            ? PromiseBB.resolve(false)
            : PromiseBB.reject(unknownToError(err)));
    }
    canRestore() {
        return true;
    }
}
function init(context) {
    context.registerDeploymentMethod(new DeploymentMethod(context.api));
    return true;
}
export default init;
